import type Filter from "fuse.js";

import type { JSX } from "react";

export interface Group {
  /** Id for this group. */
  groupId: string;
  /** The element whose DOM node this group is portaled to for rendering. */
  portal: JSX.Element;
}

export interface Item {
  /** This item's parent group. */
  groupId: string;
  /** The element whose DOM node this item is portaled to for rendering. */
  portal: JSX.Element;
  /**
   * The text that will be filtered and displayed in the input when this item is selected.
   * If a string, it is displayed.
   * If an object, the required `display` property is displayed.
   *
   * See https://fusejs.io/examples.html for how to use objects in search.
   */
  text: string | { display: string; [key: string]: string };
  /**
   * The hidden value used when the item is selected. Allows internal
   * alternates to the display text and creating unique identifiers when a list of items
   * has multiple items with the same text, perhaps in different groups.
   *
   * Must be unique within a combobox. Used as an id.
   */
  value: string;
}

/** Shared state for a `Combobox` component. */
export interface Combobox {
  /**
   * The filter instance, currently Fuse.
   * It needs to know about the React tree, but we want to batch updates to it outside of React
   * for performance reasons, so don't expose it.
   */
  _filter: Filter<Item["text"]> | null;
  /** Flag for updating the filter. */
  _filterNeedsUpdate: boolean;
  /**
   * Could be derived from `highlightedValue` and `itemOrder` but stored for expediency moving
   * the highlight.
   */
  _highlightedIndex: number;
  /** Id of this combobox. */
  readonly comboboxId: string;
  /** See the Fuse docs: https://fusejs.io/api/options.html. */
  readonly filterOptions: Partial<
    Pick<
      Filter.IFuseOptions<Item["text"]>,
      | "distance"
      | "fieldNormWeight"
      | "getFn"
      | "ignoreFieldNorm"
      | "ignoreLocation"
      | "isCaseSensitive"
      | "keys"
      | "location"
      | "threshold"
      | "useExtendedSearch"
    >
  >;
  /** Group portals in the correct order for rendering. */
  readonly groupPortals: JSX.Element[];
  /** The combobox's groups, including the combobox itself, which acts as a default group. */
  readonly groups: Map<Group["groupId"], Group>;
  /** The value of the highlighted item, or the empty string if no item is highlighted. */
  readonly highlightedValue: Item["value"];
  /** Whether the select listbox is open. */
  readonly isOpen: boolean;
  /** The flat item order, for managing the highlight and detecting empty results. */
  readonly itemOrder: Item["value"][];
  /** Item portals in the correct order and grouping for rendering. */
  readonly itemPortals: Map<Item["groupId"], JSX.Element[]>;
  /** The combobox's items. */
  readonly items: Map<Item["value"], Item>;
  /** See the Combobox prop. */
  readonly keepOpen: boolean;
  /**
   * Flag for batching filter updates. We want to filter and sort whenever groups or items are
   * added or removed or filter options change, but this is not cheap, so we put it off.
   */
  readonly needsFilter: boolean;
  /** The value of the selected item, or the empty string if no item is selected. */
  readonly selectedValue: Item["value"];
}

export const initialCombobox: Omit<Combobox, "comboboxId"> = {
  _filter: null,
  _filterNeedsUpdate: false,
  _highlightedIndex: 0,
  filterOptions: {},
  groupPortals: [],
  groups: new Map(),
  highlightedValue: "",
  isOpen: false,
  itemOrder: [],
  itemPortals: new Map(),
  items: new Map(),
  keepOpen: false,
  needsFilter: false,
  selectedValue: "",
};

export interface Comboboxes {
  [ComboboxId: string]: Combobox;
}

export const initialComboboxes: Comboboxes = {};

export interface State {
  /** Id-indexed `Combobox`. */
  comboboxes: Comboboxes;
}

export const initialState: State = {
  comboboxes: initialComboboxes,
};
